import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  const slides = [
    {
      id: 1,
      title: "Smart Solutions for Modern Living",
      subtitle: "Premium Resin Furniture",
      description: "Whether suspended, floor-mounted or shaped finished over base, our custom collection of specialty items.",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1920&h=1080&fit=crop",
      cta: "Shop Collection"
    },
    {
      id: 2,
      title: "Minimalism at its finest",
      subtitle: "Custom Epoxy Tables",
      description: "Handcrafted furniture that combines natural wood beauty with stunning epoxy resin artistry.",
      image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1920&h=1080&fit=crop",
      cta: "Explore Designs"
    },
    {
      id: 3,
      title: "Vented Hobs Nivel",
      subtitle: "Ocean-Inspired Collection", 
      description: "Transform your space with furniture that captures the essence of flowing water and natural elements.",
      image: "https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=1920&h=1080&fit=crop",
      cta: "Find Out More"
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Images */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0 z-0"
        >
          <img
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"></div>
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
          {/* Left Content */}
          <motion.div
            key={`content-${currentSlide}`}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-accent-400 text-lg font-medium mb-4"
            >
              {slides[currentSlide].subtitle}
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hero-text text-white mb-6"
            >
              {slides[currentSlide].title}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-xl text-dark-200 mb-8 max-w-xl leading-relaxed"
            >
              {slides[currentSlide].description}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/products" className="btn-primary">
                {slides[currentSlide].cta}
              </Link>
              <Link to="/about" className="btn-secondary">
                Learn More
              </Link>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-12 flex items-center space-x-8"
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-400">500+</div>
                <div className="text-sm text-dark-300">Happy Clients</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-400">6</div>
                <div className="text-sm text-dark-300">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-400">98%</div>
                <div className="text-sm text-dark-300">Satisfaction Rate</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Featured Product Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-dark-800/30 backdrop-blur-md rounded-3xl p-8 border border-white/10">
                <h3 className="text-xl font-semibold text-white mb-4">Featured Collection</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-dark-700/50 rounded-xl p-4 hover:bg-dark-700/70 transition-colors duration-300">
                    <div className="w-full h-32 bg-gradient-to-br from-blue-400 to-cyan-600 rounded-lg mb-3"></div>
                    <h4 className="font-medium text-white text-sm">River Tables</h4>
                    <p className="text-dark-300 text-xs">From $2,400</p>
                  </div>
                  <div className="bg-dark-700/50 rounded-xl p-4 hover:bg-dark-700/70 transition-colors duration-300">
                    <div className="w-full h-32 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-lg mb-3"></div>
                    <h4 className="font-medium text-white text-sm">Coffee Tables</h4>
                    <p className="text-dark-300 text-xs">From $1,200</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex items-center space-x-6">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
          >
            <ChevronLeft className="h-5 w-5 text-white" />
          </button>

          {/* Play/Pause Button */}
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-14 h-14 bg-accent-500 hover:bg-accent-600 rounded-full flex items-center justify-center transition-colors duration-300"
          >
            {isPlaying ? (
              <Pause className="h-6 w-6 text-white" />
            ) : (
              <Play className="h-6 w-6 text-white ml-1" />
            )}
          </button>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
          >
            <ChevronRight className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center space-x-2 mt-4">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-accent-400 scale-125'
                  : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 right-8 text-white/60"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm font-medium">Scroll</span>
          <div className="w-0.5 h-8 bg-white/30 rounded-full overflow-hidden">
            <motion.div
              className="w-full bg-accent-400 rounded-full"
              initial={{ height: 0 }}
              animate={{ height: '100%' }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: 'reverse',
                ease: 'easeInOut'
              }}
            />
          </div>
        </div>
      </motion.div>

      {/* Floating Elements */}
      <div className="absolute top-1/4 left-10 w-64 h-64 bg-accent-500/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-accent-600/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
    </section>
  );
};

export default HeroSection;